import { useState, useEffect, useCallback } from 'react';
import { MeetingFormData } from '../interfaces/interface';
import { logger } from '../../../../logger';

const SESSION_STORAGE_KEY = 'das-finsuite-meeting-form';
const FORMATTED_CONTENT_KEY = 'das-finsuite-formatted-content';

export const useSessionPersistence = () => {
  const [hasRestoredData, setHasRestoredData] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);

  const saveFormDataToSession = useCallback(
    (data: MeetingFormData, formattedContent?: string) => {
      if (autoSaveEnabled) {
        try {
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
          if (formattedContent !== undefined) {
            sessionStorage.setItem(FORMATTED_CONTENT_KEY, formattedContent);
          }
        } catch (error) {
          logger.warn('Failed to save form data to session storage', { error });
        }
      }
    },
    [autoSaveEnabled]
  );

  const saveImmediately = useCallback(
    (formData: MeetingFormData, documentDescription?: string) => {
      saveFormDataToSession(formData, documentDescription);
    },
    [saveFormDataToSession]
  );

  const restoreFromSession = useCallback((): {
    formData?: MeetingFormData;
    formattedContent?: string;
  } => {
    try {
      const savedFormData = sessionStorage.getItem(SESSION_STORAGE_KEY);
      const savedFormattedContent = sessionStorage.getItem(FORMATTED_CONTENT_KEY);

      const result: { formData?: MeetingFormData; formattedContent?: string } = {};

      if (savedFormData) {
        result.formData = JSON.parse(savedFormData);
      }

      if (savedFormattedContent) {
        result.formattedContent = savedFormattedContent;
      }

      return result;
    } catch (error) {
      logger.warn('Failed to restore data from session storage', { error });
      return {};
    }
  }, []);

  const clearSession = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(FORMATTED_CONTENT_KEY);
    } catch (error) {
      logger.warn('Failed to clear session storage', { error });
    }
  }, []);

  useEffect(() => {
    setHasRestoredData(true);
  }, []);

  return {
    hasRestoredData,
    autoSaveEnabled,
    saveFormDataToSession,
    saveImmediately,
    restoreFromSession,
    clearSession,
    setAutoSaveEnabled
  };
};
